import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal, unstable_batchedUpdates as unstableBatchedUpdates } from 'react-dom';
import {
  closestCenter,
  CollisionDetection,
  defaultDropAnimationSideEffects,
  DndContext,
  DragOverlay,
  DropAnimation,
  getFirstCollision,
  KeyboardSensor,
  MeasuringStrategy,
  MouseSensor,
  pointerWithin,
  rectIntersection,
  TouchSensor,
  UniqueIdentifier,
  useSensor,
  useSensors
} from '@dnd-kit/core';

import {
  AnimateLayoutChanges,
  arrayMove,
  defaultAnimateLayoutChanges,
  rectSortingStrategy,
  SortableContext,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';

import { type Items } from 'types';

import { CSS } from '@dnd-kit/utilities';
import { type Category, Product } from 'generated/graphql';
import { useCategoryContext } from 'components/CategoryList/CategoryContext';
import { coordinateGetter } from './Utilities/CoordinateGetter';

import { Item } from './Item/Item';
import type { Props as ContainerProps } from './DraggableCategory/DraggableCategory';
import DraggableCategory from './DraggableCategory/DraggableCategory';

import styles from './DraggableContainer.module.scss';
import { CATEGORY_ITEM_IDENTIFIER } from '../../utils';

const animateLayoutChanges: AnimateLayoutChanges = (args) =>
  defaultAnimateLayoutChanges({ ...args, wasDragging: true });

const DroppableContainer: React.FC<
  ContainerProps & {
    disabled?: boolean;
    id: UniqueIdentifier;
    items: UniqueIdentifier[];
    style?: React.CSSProperties;
  }
> = ({ children, disabled, id, items, category, style, ...props }) => {
  const { active, attributes, isDragging, listeners, over, setNodeRef, transition, transform } = useSortable({
    id,
    data: {
      type: 'container',
      children: items
    },
    animateLayoutChanges
  });

  const isOverContainer = over
    ? (id === over.id && active?.data.current?.type !== 'container') || items.includes(over.id)
    : false;

  return (
    <DraggableCategory
      ref={disabled ? undefined : setNodeRef}
      style={{
        ...style,
        transition,
        transform: CSS.Translate.toString(transform),
        opacity: isDragging ? 0.5 : undefined
      }}
      hover={isOverContainer}
      handleProps={{
        ...attributes,
        ...listeners
      }}
      category={category}
      {...props}
    >
      {children}
    </DraggableCategory>
  );
};

const dropAnimation: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: '0.5'
      }
    }
  })
};

interface SortableItemProps {
  id: UniqueIdentifier;
  index: number;
  disabled?: boolean;
  item: Product;
  categoryId: string;
  wrapperStyle({ index }: { index: number }): React.CSSProperties;
  isLoading?: boolean;
}

const SortableItem: React.FC<SortableItemProps> = ({
  disabled,
  id,
  index,
  item,
  wrapperStyle,
  isLoading,
  categoryId
}) => {
  const { setNodeRef, listeners, isDragging, transform, transition } = useSortable({
    id
  });

  return (
    <Item
      ref={disabled ? undefined : setNodeRef}
      dragging={isDragging}
      wrapperStyle={wrapperStyle({ index })}
      transition={transition}
      item={item}
      transform={transform}
      categoryId={categoryId}
      listeners={listeners}
      isLoading={isLoading}
    />
  );
};

interface Props {
  wrapperStyle?(args: { index: number }): React.CSSProperties;
  categories: Category[];
  products: Product[];
}

const PLACEHOLDER_ID = 'placeholder';

type GetMenuRow = {
  item: Product;
  uniquePosId: string;
};

export const DraggableContainer: React.FC<Props> = ({ categories, wrapperStyle = () => ({}), products }) => {
  const {
    handleToggleCategoryCollapse,
    collapsedCategories,
    isLoading,
    items,
    setItems,
    containers,
    setContainers,
    reorderCategories,
    reorderProducts
  } = useCategoryContext();
  const categoryNames: string[] = categories.map((category) => category.category.toLowerCase());
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const lastOverId = useRef<UniqueIdentifier | null>(null);
  const recentlyMovedToNewContainer = useRef(false);
  const isSortingContainer = activeId ? containers.includes(activeId) : false;
  const previousDragData = useRef<{
    activeId: UniqueIdentifier | null;
    currentParent: UniqueIdentifier | null;
    currentIndex: number | null;
  }>({
    activeId: null,
    currentParent: null,
    currentIndex: null
  });

  const collisionDetectionStrategy: CollisionDetection = useCallback(
    (args) => {
      if (activeId && activeId in items) {
        return closestCenter({
          ...args,
          droppableContainers: args.droppableContainers.filter((container) => container.id in items)
        });
      }

      // Start by finding any intersecting droppable
      const pointerIntersections = pointerWithin(args);
      const intersections = pointerIntersections.length > 0 ? pointerIntersections : rectIntersection(args);
      let overId = getFirstCollision(intersections, 'id');

      if (overId != null) {
        if (overId in items) {
          const containerItems = items[overId];

          if (containerItems.length > 0) {
            overId = closestCenter({
              ...args,
              droppableContainers: args.droppableContainers.filter(
                (container) => container.id !== overId && containerItems.includes(container.id)
              )
            })[0]?.id;
          }
        }

        lastOverId.current = overId;

        return [{ id: overId }];
      }

      if (recentlyMovedToNewContainer.current) {
        lastOverId.current = activeId;
      }

      return lastOverId.current ? [{ id: lastOverId.current }] : [];
    },
    [activeId, items]
  );

  const [clonedItems, setClonedItems] = useState<Items | null>(null);
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8
      }
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        tolerance: 0,
        delay: 100
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter,
      keyboardCodes: {
        start: ['Space'],
        end: ['Space'],
        cancel: ['Escape']
      }
    })
  );
  const findContainer = useCallback(
    (id: UniqueIdentifier): UniqueIdentifier | undefined => {
      if (id in items) {
        return id;
      }

      return Object.keys(items).find((key) => items[key].includes(id));
    },
    [items]
  );

  const onDragCancel = (): void => {
    if (clonedItems) {
      // Reset items to their original state in case items have been
      // Dragged across containers
      setItems(clonedItems);
    }

    setActiveId(null);
    setClonedItems(null);
  };

  useEffect(() => {
    requestAnimationFrame(() => {
      recentlyMovedToNewContainer.current = false;
    });
  }, [items]);

  const getMenuItems = (containerId: UniqueIdentifier): GetMenuRow[] => {
    const categoryProductIds = items[containerId];

    return categoryProductIds
      .map((posId: UniqueIdentifier) => {
        return {
          item: products.find(
            (product: Product) =>
              posId &&
              (product.customPosId === posId.toString().split(CATEGORY_ITEM_IDENTIFIER)[0] ||
                product.posId === posId.toString().split(CATEGORY_ITEM_IDENTIFIER)[0])
          ),
          uniquePosId: posId
        };
      })
      .filter((row) => row.item) as GetMenuRow[];
  };

  const getCategory = (id: UniqueIdentifier): Category | undefined => {
    return categories.find((category) => category.mmId === id);
  };

  const getNextContainerId = (): string => {
    const containerIds = Object.keys(items);
    const lastContainerId = containerIds[containerIds.length - 1];

    return String.fromCharCode(lastContainerId.charCodeAt(0) + 1);
  };

  const renderSortableItemDragOverlay = (id: UniqueIdentifier): JSX.Element => {
    const idWithoutCategory = id.toString().split(CATEGORY_ITEM_IDENTIFIER)[0];

    const item = products.find(
      (product: Product) => product.customPosId === idWithoutCategory || product.posId === idWithoutCategory
    ) as Product;

    return <Item categoryId={id.toString()} item={item} isDragOverlay wrapperStyle={wrapperStyle({ index: 0 })} />;
  };

  const renderContainerDragOverlay = (containerId: UniqueIdentifier): JSX.Element => {
    const category = getCategory(containerId);

    return <DraggableCategory category={category} shadow unstyled={false} categoryNames={[]} isDragOverlay />;
  };

  useEffect(() => {
    if (previousDragData.current.activeId && !activeId) {
      const { activeId, currentIndex, currentParent } = previousDragData.current;

      if (!currentParent) {
        const newIndex = containers.indexOf(activeId);
        if (newIndex === currentIndex) return;
        reorderCategories(newIndex, currentIndex as number);
      } else {
        const itemIndexInCurrentParent = items[currentParent].indexOf(activeId);

        if (itemIndexInCurrentParent === -1) {
          const newParent = findContainer(activeId);
          if (!newParent) return;
          const itemIndexInNewParent = items[newParent].indexOf(activeId);
          reorderProducts(activeId, itemIndexInNewParent, currentIndex as number, currentParent, newParent);
        } else if (itemIndexInCurrentParent !== currentIndex) {
          reorderProducts(activeId, itemIndexInCurrentParent, currentIndex as number, currentParent);
        }
      }

      previousDragData.current = {
        activeId: null,
        currentIndex: null,
        currentParent: null
      };
    }
  }, [items, activeId, containers, findContainer, reorderCategories, reorderProducts]);
  const getItemNameById = (id: UniqueIdentifier): string => {
    const _id = id.toString().split(CATEGORY_ITEM_IDENTIFIER)[0];

    let response: Product | Category | undefined = products.find((product) => {
      return product.posId === _id;
    });

    if (response) {
      return response.name;
    }

    response = categories.find((category) => category.mmId === _id);

    if (response) {
      return response.category;
    }

    return '';
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetectionStrategy}
      measuring={{
        droppable: {
          strategy: MeasuringStrategy.Always
        }
      }}
      accessibility={{
        announcements: {
          onDragStart({ active }): string {
            const itemName = getItemNameById(active.id);

            return `Picked up ${itemName}`;
          },
          onDragOver({ active, over }): string | undefined {
            if (over) {
              const itemName = getItemNameById(active.id);

              return `Sortable item ${itemName} was moved`;
            }

            return undefined;
          },
          onDragEnd({ active, over }): string | undefined {
            if (over) {
              const itemName = getItemNameById(active.id);

              return `Dropped ${itemName}.`;
            }

            return undefined;
          },
          onDragCancel({ active }): string {
            const itemName = getItemNameById(active.id);

            return `Dragging was cancelled. Sortable item ${itemName} was dropped.`;
          }
        }
      }}
      onDragStart={({ active }) => {
        if (active?.data?.current?.type === 'container') {
          const { id } = active;

          const isCollapsed = collapsedCategories.includes(id.toString());

          if (!isCollapsed) {
            handleToggleCategoryCollapse(id.toString());
          }
        }

        const currentIndex = active?.data?.current?.sortable?.index;
        const currentParent = findContainer(active.id) || null;
        const activeId = active.id;

        previousDragData.current = {
          activeId,
          currentIndex,
          currentParent: active?.data?.current?.type === 'container' ? null : currentParent
        };

        setActiveId(active.id);
        setClonedItems(items);
      }}
      onDragOver={({ active, over }) => {
        const overId = over?.id;

        if (overId == null || active.id in items) {
          return;
        }

        const overContainer = findContainer(overId);
        const activeContainer = findContainer(active.id);

        if (!overContainer || !activeContainer) {
          return;
        }

        if (activeContainer !== overContainer) {
          setItems((items) => {
            const activeItems = items[activeContainer];
            const overItems = items[overContainer];
            const overIndex = overItems.indexOf(overId);
            const activeIndex = activeItems.indexOf(active.id);

            let newIndex: number;

            if (overId in items) {
              newIndex = overItems.length + 1;
            } else {
              const isBelowOverItem =
                over &&
                active.rect.current.translated &&
                active.rect.current.translated.top > over.rect.top + over.rect.height;

              const modifier = isBelowOverItem ? 1 : 0;

              newIndex = overIndex >= 0 ? overIndex + modifier : overItems.length + 1;
            }

            recentlyMovedToNewContainer.current = true;

            return {
              ...items,
              [activeContainer]: items[activeContainer].filter((item) => item !== active.id),
              [overContainer]: [
                ...items[overContainer].slice(0, newIndex),
                items[activeContainer][activeIndex],
                ...items[overContainer].slice(newIndex, items[overContainer].length)
              ]
            };
          });
        }
      }}
      onDragEnd={({ active, over }) => {
        if (active.id in items && over?.id) {
          setContainers((containers) => {
            const activeIndex = containers.indexOf(active.id);
            const overIndex = containers.indexOf(over.id);

            return arrayMove(containers, activeIndex, overIndex);
          });
        }

        const activeContainer = findContainer(active.id);

        if (!activeContainer) {
          setActiveId(null);
          return;
        }

        const overId = over?.id;

        if (overId == null) {
          setActiveId(null);
          return;
        }

        if (overId === PLACEHOLDER_ID) {
          const newContainerId = getNextContainerId();

          unstableBatchedUpdates(() => {
            setContainers((containers) => [...containers, newContainerId]);

            setItems((items) => ({
              ...items,
              [activeContainer]: items[activeContainer].filter((id) => id !== activeId),
              [newContainerId]: [active.id]
            }));
            setActiveId(null);
          });
          return;
        }

        const overContainer = findContainer(overId);

        if (overContainer) {
          const activeIndex = items[activeContainer].indexOf(active.id);
          const overIndex = items[overContainer].indexOf(overId);

          if (activeIndex !== overIndex) {
            setItems((items) => ({
              ...items,
              [overContainer]: arrayMove(items[overContainer], activeIndex, overIndex)
            }));
          }
        }

        setActiveId(null);
      }}
      onDragCancel={onDragCancel}
    >
      <div className={styles.Outer}>
        <SortableContext items={[...containers, PLACEHOLDER_ID]} strategy={verticalListSortingStrategy}>
          {containers.map((containerId) => {
            const categoryProductIds = items[containerId];

            const menuItemRows: GetMenuRow[] = getMenuItems(containerId);
            const category = getCategory(containerId);
            if (!category) {
              return null;
            }

            return (
              <DroppableContainer
                key={containerId}
                id={containerId}
                items={categoryProductIds}
                category={category}
                categoryNames={categoryNames.filter((name) => name.toLowerCase() !== category.category.toLowerCase())}
              >
                <SortableContext items={categoryProductIds} strategy={rectSortingStrategy}>
                  {menuItemRows.map(({ item, uniquePosId }, index) => {
                    return (
                      <SortableItem
                        disabled={isSortingContainer}
                        key={uniquePosId}
                        id={uniquePosId}
                        index={index}
                        item={item}
                        wrapperStyle={wrapperStyle}
                        isLoading={isLoading}
                        categoryId={category?.mmId}
                      />
                    );
                  })}
                </SortableContext>
              </DroppableContainer>
            );
          })}
        </SortableContext>
      </div>
      {createPortal(
        <DragOverlay dropAnimation={dropAnimation}>
          {((): JSX.Element | null => {
            if (activeId) {
              if (containers.includes(activeId)) {
                return renderContainerDragOverlay(activeId);
              }
              return renderSortableItemDragOverlay(activeId);
            }

            return null;
          })()}
        </DragOverlay>,
        document.body
      )}
    </DndContext>
  );
};
